import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import {
  getApiV1LocationCountriesQueryOptions,
  usePostApiV1LocationCountries,
  usePutApiV1LocationCountriesId,
} from '@/shared/api/generated';
import type { CountryFormValues } from '../types/countryForm';

/** Callbacks do salvamento de País, com caminho específico para conflito (409). */
export interface SaveCountryCallbacks {
  readonly onSuccess?: () => void;
  /** Chamado quando a API retorna 409 (sigla/nome já cadastrados). */
  readonly onConflict?: () => void;
  /** Chamado para qualquer outro erro. */
  readonly onError?: () => void;
}

/**
 * Hook para criar/editar um País.
 *
 * Encapsula os mutations gerados pelo Kubb (`POST /api/v1/location/countries` e
 * `PUT /api/v1/location/countries/{id}`), escolhendo a operação pela presença de
 * `id`, invalidando o cache da listagem no sucesso e mapeando o status **409**
 * (sigla/nome já cadastrados) para o callback `onConflict`.
 *
 * @example
 * ```tsx
 * const { save, isPending } = useSaveCountry();
 * save(values, editingId, { onSuccess, onConflict, onError });
 * ```
 */
export function useSaveCountry() {
  const queryClient = useQueryClient();
  const createMutation = usePostApiV1LocationCountries();
  const updateMutation = usePutApiV1LocationCountriesId();

  const invalidate = () => queryClient.invalidateQueries(getApiV1LocationCountriesQueryOptions());

  const handleError = (error: unknown, callbacks?: SaveCountryCallbacks) => {
    if (isAxiosError(error) && error.response?.status === 409) {
      callbacks?.onConflict?.();
    } else {
      callbacks?.onError?.();
    }
  };

  /**
   * Salva um país. Cria quando `id` é `undefined`; caso contrário, atualiza.
   */
  const save = (
    values: CountryFormValues,
    id: number | undefined,
    callbacks?: SaveCountryCallbacks,
  ) => {
    const data = { code: values.code, name: values.name, active: values.active };
    const onSuccess = () => {
      void invalidate();
      callbacks?.onSuccess?.();
    };

    if (id === undefined) {
      createMutation.mutate(
        { data },
        { onSuccess, onError: error => handleError(error, callbacks) },
      );
    } else {
      updateMutation.mutate(
        { id, data },
        { onSuccess, onError: error => handleError(error, callbacks) },
      );
    }
  };

  return {
    save,
    isPending: createMutation.isPending || updateMutation.isPending,
  } as const;
}
